'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

const DEMO_ACCOUNTS = [
  {
    label: 'Plan Pro',
    email: 'admin@demo-drivex.com',
    password: 'Demo@DriveX99!',
    badge: 'PRO',
    badgeBg: '#C8102E',
    badgeColor: '#fff',
    borderColor: 'rgba(200,16,46,0.35)',
    href: null,
  },
  {
    label: 'Plan Elite',
    email: 'elite@demo-drivex.com',
    password: 'Elite@DriveX99!',
    badge: 'ELITE',
    badgeBg: '#C9A84C',
    badgeColor: '#000',
    borderColor: 'rgba(201,168,76,0.35)',
    href: null,
  },
  {
    label: 'Super Admin',
    email: 'superadmin@drivex.app',
    password: 'DriveX@Super2024!',
    badge: 'SA',
    badgeBg: '#444',
    badgeColor: '#fff',
    borderColor: 'rgba(255,255,255,0.12)',
    href: null,
  },
];

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();

  // Load remembered credentials on mount
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('drivex_remembered_email');
    const savedPassword = localStorage.getItem('drivex_remembered_password');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  function fillDemo(account: (typeof DEMO_ACCOUNTS)[0]) {
    setEmail(account.email);
    setPassword(account.password);
    setError('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor ingresa tu correo y contraseña.');
      return;
    }
    setLoading(true);
    setError('');

    // Save or clear remembered credentials
    if (rememberMe) {
      localStorage.setItem('drivex_remembered_email', email);
      localStorage.setItem('drivex_remembered_password', password);
    } else {
      localStorage.removeItem('drivex_remembered_email');
      localStorage.removeItem('drivex_remembered_password');
    }

    try {
      const supabase = createClient();

      // Sign out any existing session first to prevent stale cache issues
      await supabase.auth.signOut({ scope: 'local' });

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });

      if (authError || !authData?.user) {
        setError('Correo o contraseña incorrectos.');
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, tenant_id')
        .eq('id', authData.user.id)
        .single();

      if (profileError || !profile) {
        setError('Perfil no encontrado. Contacta a soporte.');
        await supabase.auth.signOut({ scope: 'local' });
        setLoading(false);
        return;
      }

      if (profile.role === 'superadmin') {
        router.replace('/superadmin/tenants');
        return;
      }

      if (!profile.tenant_id) {
        router.replace('/onboarding');
        return;
      }

      const { data: tenant } = await supabase
        .from('tenants')
        .select('onboarding_completed')
        .eq('id', profile.tenant_id)
        .single();

      router.replace(tenant?.onboarding_completed ? '/admin/dashboard' : '/onboarding');
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError('Error inesperado. Intenta de nuevo.');
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #0A0A0A; }
        .lp-root {
          min-height: 100vh;
          background: #0A0A0A;
          background-image:
            linear-gradient(rgba(200,16,46,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(200,16,46,0.025) 1px, transparent 1px);
          background-size: 56px 56px;
          font-family: 'Montserrat', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 16px;
        }
        .lp-wrap {
          width: 100%;
          max-width: 460px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .lp-card {
          background: #111111;
          border: 1px solid #1E1E1E;
          border-radius: 16px;
          padding: 40px 36px;
        }
        .lp-input {
          width: 100%;
          background: #181818;
          border: 1.5px solid #2A2A2A;
          border-radius: 8px;
          padding: 11px 14px;
          font-size: 14px;
          color: #FFFFFF;
          font-family: 'Montserrat', sans-serif;
          outline: none;
          transition: border-color 0.15s;
        }
        .lp-input::placeholder { color: #444; }
        .lp-input:focus { border-color: #C8102E; box-shadow: 0 0 0 3px rgba(200,16,46,0.1); }
        .lp-btn-primary {
          width: 100%;
          background: #C8102E;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 13px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .lp-btn-primary:disabled { background: #7A0A1C; cursor: not-allowed; }
        .lp-btn-primary:not(:disabled):hover { background: #E01235; }
        .lp-demo-card {
          background: #111111;
          border: 1px solid #1E1E1E;
          border-radius: 16px;
          padding: 24px 28px;
        }
        .lp-demo-row {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 8px;
          border: 1px solid #222;
          background: #161616;
          margin-bottom: 8px;
          transition: border-color 0.15s;
        }
        .lp-demo-row:last-child { margin-bottom: 0; }
        .lp-use-btn {
          background: #1E1E1E;
          border: 1px solid #333;
          border-radius: 6px;
          color: #AAAAAA;
          font-size: 11px;
          font-weight: 700;
          font-family: 'Montserrat', sans-serif;
          letter-spacing: 0.06em;
          padding: 6px 12px;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
          text-transform: uppercase;
        }
        .lp-use-btn:hover { background: #C8102E; color: #fff; border-color: #C8102E; }
        .spin {
          width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: lp-spin 0.7s linear infinite;
          display: inline-block;
          flex-shrink: 0;
        }
        @keyframes lp-spin { to { transform: rotate(360deg); } }
        @media (max-width: 480px) {
          .lp-card { padding: 28px 20px; }
          .lp-demo-card { padding: 20px 16px; }
        }
      `}</style>

      <div className="lp-root">
        <div className="lp-wrap">

          {/* ── Login card ── */}
          <div className="lp-card">
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ fontWeight: 900, fontSize: '26px', color: '#FFFFFF', letterSpacing: '0.08em', marginBottom: '4px' }}>
                Drive<span style={{ color: '#C8102E' }}>X</span>
              </div>
              <div style={{ fontSize: '10px', color: '#555', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                Panel de administración
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'rgba(200,16,46,0.08)',
                border: '1px solid rgba(200,16,46,0.3)',
                borderRadius: '8px',
                padding: '11px 14px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#FF6B6B',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}>
                <span style={{ flexShrink: 0 }}>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#666', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="lp-input"
                  autoComplete="email"
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#666', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Contraseña
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="lp-input"
                  autoComplete={rememberMe ? 'current-password' : 'off'}
                />
              </div>

              {/* Remember Me */}
              <div style={{ marginBottom: '22px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ width: '14px', height: '14px', accentColor: '#C8102E', cursor: 'pointer', flexShrink: 0 }}
                />
                <label htmlFor="rememberMe" style={{ fontSize: '12px', color: '#888888', cursor: 'pointer', userSelect: 'none' }}>
                  Recordar mis datos
                </label>
              </div>

              <button type="submit" disabled={loading} className="lp-btn-primary">
                {loading ? (
                  <>
                    <span className="spin" />
                    Iniciando sesión...
                  </>
                ) : 'Iniciar sesión'}
              </button>
            </form>
          </div>

          {/* ── Demo credentials card ── */}
          <div className="lp-demo-card">
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#444', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: '14px' }}>
              Cuentas de demostración
            </div>

            {DEMO_ACCOUNTS.map((account) => (
              <div
                key={account.email}
                className="lp-demo-row"
                style={{ borderColor: account.borderColor }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF' }}>
                      {account.label}
                    </span>
                    <span style={{
                      fontSize: '9px',
                      fontWeight: 800,
                      background: account.badgeBg,
                      color: account.badgeColor,
                      padding: '2px 7px',
                      borderRadius: '4px',
                      letterSpacing: '0.1em',
                    }}>
                      {account.badge}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px', fontFamily: 'monospace' }}>
                    {account.email}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>
                    {account.password}
                  </div>
                </div>

                {account.href ? (
                  <a
                    href={account.href}
                    style={{
                      background: '#1E1E1E',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#AAAAAA',
                      fontSize: '11px',
                      fontWeight: 700,
                      fontFamily: 'Montserrat, sans-serif',
                      letterSpacing: '0.06em',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    Ir →
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => fillDemo(account)}
                    className="lp-use-btn"
                  >
                    Usar
                  </button>
                )}
              </div>
            ))}

            <p style={{ fontSize: '11px', color: '#3A3A3A', marginTop: '12px', textAlign: 'center' }}>
              Haz clic en <strong style={{ color: '#555' }}>Usar</strong> para autocompletar el formulario
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
